import React, { FC, useState, useContext, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import moment from 'moment';
import noop from 'lodash/noop';
import omit from 'lodash/omit';
import produce from 'immer';
import remove from 'lodash/remove';
import set from 'lodash/set';
import { useDispatch } from 'react-redux';

import { Button } from 'src/presentation/components/agent_desktop_components';
import { ButtonTypes } from '@ad-scripts/presentation/components/common/button';
import { calculateNodesPositionsForTemplate } from 'src/presentation/components/pages/ivr/components/ivrTree/helpers/nodesHelper';
import {
  DATE_FIELD,
  DAYS_OF_WEEK_FIELD,
  LOCATION_FIELD,
  NODE_ID_FIELD,
  TIME_RANGES_FOR_DATE_FIELD,
  TIME_RANGES_FOR_WEEKDAY_FIELD,
  TYPE_FIELD,
} from 'src/presentation/components/pages/ivr/constants/scheduleConstants';
import DatePicker from 'src/presentation/components/forms/DatePicker';
import { DAYS_OF_THE_WEEK } from 'src/presentation/components/pages/shared/constants/dateTimeConstants';
import {
  DAYS_OF_THE_WEEK_OPTIONS,
  SCHEDULE_TYPES_OPTIONS,
} from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import FormContext from 'src/presentation/contexts/FormContext';
import { getConfigurationFromTemplate } from 'src/presentation/components/pages/ivr/components/ivrTree/helpers/newNodesHelper';
import { isOutdatedSchedule } from 'src/presentation/components/pages/ivr/helpers/ivrHelpers';
import { IvrConfig } from 'src/presentation/components/pages/ivr/components/ivrTree/config/ivrConfig';
import { IvrEditorContext } from 'src/presentation/components/pages/ivr/IvrEditor';
import ivrResource from 'src/actions/ivrs/ivrResource';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import ivrsTreeDataResource from 'src/actions/ivrs/ivrsTreeDataResource';
import { IvrTemplateType } from 'src/presentation/components/pages/ivr/constants/templates/index';
import { MultiContentRow, Row, ActionRow } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import { NewNodeType } from 'src/presentation/components/pages/ivr/hooks/useCreateNewNode';
import { NodeScheduleItem } from 'src/models/Ivrs/Nodes/ScheduleNodeDef';
import NodeSelector from 'src/presentation/components/pages/ivr/components/NodeSelector';
import organizationResource from 'src/actions/organization/organizationResource';
import { ScheduleShortPresentation } from './ScheduleShortPresentation';
import { ScheduleType, ScheduleBlock } from 'src/models/Ivrs/Nodes/ScheduleNodeDef';
import {
  SHORT_VIEW_DATE_FORMAT,
  SHORT_VIEW_TIME_FORMAT,
  STORED_DATETIME_FORMAT,
} from 'src/presentation/components/pages/shared/constants/dateTimeConstants';
import { TimeRanges } from 'src/presentation/components/pages/ivr/components/TimeRanges';
import { TIMEZONE_OPTIONS } from 'src/presentation/components/pages/shared/constants/dateTimeConstants';
import useCreateNew from 'src/presentation/components/pages/ivr/hooks/useCreateNew';
import { useResourceValue } from 'src/presentation/hooks/useDocumentResource';

const isScheduleTypeWeekDay = schedule => schedule.schedule.type === ScheduleType.SCHEDULE_TYPE_WEEKDAY;
const getTimeRangesField = schedule =>
  isScheduleTypeWeekDay(schedule) ? TIME_RANGES_FOR_WEEKDAY_FIELD : TIME_RANGES_FOR_DATE_FIELD;

type EditScheduleProps = {
  'data-aid'?: string;
  attr: string;
  currentIndex: number;
  currentSchedule: NodeScheduleItem;
  disabled?: boolean;
  nodeAttr: string;
  nodeList: any;
  omittedNodeIds?: string[];
  onDelete?: Function;
  onSelectNode?: Function;
  onSwitchToShortPresentation: Function;
  openInEditMode: boolean;
  readOnly?: boolean;
};

export const EditSchedule: FC<EditScheduleProps> = ({
  'data-aid': dataAid,
  attr,
  currentIndex,
  currentSchedule,
  disabled,
  nodeAttr,
  nodeList,
  omittedNodeIds,
  onDelete,
  onSelectNode,
  onSwitchToShortPresentation,
  openInEditMode,
  readOnly,
}) => {
  const dispatch = useDispatch();
  const { setIsSaveInProgress } = useContext(IvrEditorContext);

  const { validateConfiguration, validateSchedule } = ivrResource.effects;

  const { value: positions } = useResourceValue(ivrsTreeDataResource);
  const { value: organization } = useResourceValue(organizationResource);

  const [editMode, setEditMode] = useState(openInEditMode);

  const { newNode, options, onCreateNewNode, onCreateNewNodesFromTemplate } = useCreateNew({ nodeList });

  const { onBlur, overrideState, workingState } = useContext(FormContext);

  const [schedule, setSchedule] = useState<NodeScheduleItem>(currentSchedule);
  const scheduleType = get(schedule, TYPE_FIELD);
  const isDaysOfWeekSchedule = scheduleType === ScheduleType.SCHEDULE_TYPE_WEEKDAY;

  const isNew = !get(workingState, attr);

  const date = get(schedule, DATE_FIELD, '');
  const daysOfTheWeek = get(schedule, DAYS_OF_WEEK_FIELD, []);
  const timeZone = get(schedule, LOCATION_FIELD);
  const nodeId = get(schedule, NODE_ID_FIELD, '');

  const timeRanges: any = useMemo(() => {
    const timeRangeField = getTimeRangesField(schedule);
    return get(schedule, timeRangeField, []);
  }, [schedule]);

  const onChange = (attr, event) => {
    let value = get(event, 'target.value', event);
    const clonedSchedule = cloneDeep(schedule);
    if (attr === DATE_FIELD) {
      // added this date re-formating because date '1/1/2020' is not supported on BE
      // it should be '01/01/2020'
      // added here to prevent regressions on other part of code
      value = moment(value, 'MM/DD/YYYY').format('MM/DD/YYYY');
    }
    if (attr === TYPE_FIELD) {
      // replace TimeRanges<->timeRanges values
      isScheduleTypeWeekDay(schedule)
        ? set(clonedSchedule, TIME_RANGES_FOR_DATE_FIELD, get(schedule, TIME_RANGES_FOR_WEEKDAY_FIELD))
        : set(clonedSchedule, TIME_RANGES_FOR_WEEKDAY_FIELD, get(schedule, TIME_RANGES_FOR_DATE_FIELD));
    }
    if (isNewNodeOrTemplate(attr, value)) {
      const node = value.node;
      if (value.type === NewNodeType.NODE) {
        onCreateNewNode(node);
        setSchedule(set(clonedSchedule, attr, node.ivrNode.id));
        return;
      }
      onCreateNewNodesFromTemplate(value.templateId);
      setSchedule(set(clonedSchedule, attr, value.templateId));
      return;
    }

    setSchedule(set(clonedSchedule, attr, value));
  };

  const isNewNodeOrTemplate = (attr, value) => attr === 'nodeId' && typeof value !== 'string';

  const onMultiSelectChange = (attr, event) => {
    const value = get(event, 'target.value', event);

    const clonedSchedule = cloneDeep(schedule);
    let currentValue: string[] = get(clonedSchedule, attr, []);
    const index = findIndex(currentValue, val => val === value);
    if (index === -1) {
      currentValue.push(value);
    } else {
      currentValue = remove(currentValue, val => val !== value);
    }

    setSchedule(set(clonedSchedule, attr, currentValue));
  };

  const dateComponent = isDaysOfWeekSchedule ? (
    <Dropdown
      attr={`${attr}.${DAYS_OF_WEEK_FIELD}`}
      data-aid={`${dataAid}-schedule-daysOfTheWeek`}
      isMultiSelect
      label="Days of week"
      placeholder="Select days of week"
      options={DAYS_OF_THE_WEEK_OPTIONS}
      readOnly={readOnly}
      onChange={e => onMultiSelectChange(DAYS_OF_WEEK_FIELD, e)}
      value={daysOfTheWeek}
    />
  ) : (
    <DatePicker
      attr={`${attr}.${DATE_FIELD}`}
      data-aid={`${dataAid}-schedule-date`}
      label="Date"
      readOnly={readOnly}
      onChange={e => onChange(DATE_FIELD, e)}
      value={date}
    />
  );

  const onTimeRangeChanged = e => {
    const attr = e.attr === 'stop' ? 'end' : e.attr;
    setSchedule(
      produce(schedule, draft => {
        set(draft, `${getTimeRangesField(draft)}.${e.index}.${attr}`, e.value);
      })
    );
  };

  const onTimeRangeAdd = newItem => {
    setSchedule(
      produce(schedule, draft => {
        set(draft, getTimeRangesField(draft), newItem);
      })
    );
  };

  const onTimeRangeDelete = index => {
    setSchedule(
      produce(schedule, draft => {
        get(draft, getTimeRangesField(draft), []).splice(index, 1);
      })
    );
  };

  const handleCancel = () => {
    setSchedule(currentSchedule);
    setEditMode(false);
    onSwitchToShortPresentation();
  };

  const saveScheduleWithTemplateHandler = schedule => {
    setIsSaveInProgress(true);

    const configurationFromTemplate = getConfigurationFromTemplate(schedule.nodeId, organization);

    const parentId = get(workingState, `${nodeAttr}.id`);

    const templateConfig = new IvrConfig(configurationFromTemplate);
    const templateNodesLocations = calculateNodesPositionsForTemplate({
      locationState: positions,
      parentId,
      templateConfig,
    });

    schedule.nodeId = templateConfig.rootNode.ivrNode.id;

    const state = produce<any>(workingState, draft => {
      if (isNew) {
        const schedules = get(draft, `${nodeAttr}.nodeSchedules`, []);
        schedules.push(schedule);

        templateConfig.nodesMap;

        draft.nodesMap = { ...draft.nodesMap, ...templateConfig.nodesMap }; // [nodeId] = newNode;
      } else {
        set(draft, `${attr}`, schedule);
      }
    });

    dispatch(validateConfiguration(state));
    const scheduleIsValid = dispatch(validateSchedule(schedule));

    if (!scheduleIsValid) {
      return;
    }

    dispatch(ivrsResource.effects.updateWithCoordinates({ config: state, positions: templateNodesLocations }));
    setEditMode(false);
    setSchedule(set(schedule, attr, parentId));
    overrideState && overrideState(state);
    onSwitchToShortPresentation();
  };

  const onSave = () => {
    let clonedSchedule = cloneDeep(schedule);

    if (Object.values(IvrTemplateType).includes(clonedSchedule.nodeId as IvrTemplateType)) {
      saveScheduleWithTemplateHandler(clonedSchedule);
      return;
    }

    const state = produce(workingState, draft => {
      if (isNew) {
        const schedules: any[] = get(draft, `${nodeAttr}.nodeSchedules`, []);
        schedules.push(clonedSchedule);
      } else {
        set(draft, `${attr}`, clonedSchedule);
      }
    });

    dispatch(validateConfiguration(state));
    const scheduleIsValid = dispatch(validateSchedule(clonedSchedule));

    if (!scheduleIsValid) {
      return;
    }

    // We need to remove redundant property `timeRanges` for `SCHEDULE_TYPE_WEEKDAY` or
    // `TimeRanges` for `SCHEDULE_TYPE_DATE` schedule
    // because on BE we have different fields `timeRanges` and `TimeRanges`
    clonedSchedule = omit(
      clonedSchedule,
      isScheduleTypeWeekDay(schedule) ? TIME_RANGES_FOR_DATE_FIELD : TIME_RANGES_FOR_WEEKDAY_FIELD
    );

    setEditMode(false);

    const newState = produce(workingState, (draft: any) => {
      if (currentIndex === -1) {
        const schedules = get(draft, `${nodeAttr}.nodeSchedules`, []);
        schedules.push(clonedSchedule);
      } else {
        set(draft, attr, clonedSchedule);
      }

      if (newNode) {
        const nodeId = get(newNode, 'ivrNode.id', '');
        draft.nodesMap[nodeId] = newNode;
      }
    });

    overrideState && overrideState(newState);

    onBlur &&
      onBlur({
        currentState: newState,
      });

    if (newNode) {
      onSelectNode && onSelectNode(get(newNode, 'ivrNode.id'));
    }

    onSwitchToShortPresentation();
  };

  const renderShortView = () => {
    return (
      <ViewMode
        data-aid={dataAid}
        date={date}
        daysOfTheWeek={daysOfTheWeek}
        disabled={readOnly || disabled}
        isOutdatedSchedule={isOutdatedSchedule(schedule)}
        nodeId={nodeId}
        nodeList={options}
        onDelete={() => onDelete && onDelete(currentIndex)}
        onSelectNode={onSelectNode}
        setEditMode={setEditMode}
        timeRanges={timeRanges}
        timeZone={timeZone}
        type={scheduleType}
      />
    );
  };

  const renderForm = () => (
    <>
      <Row>
        <Dropdown
          attr={`${attr}.schedule.type}`}
          data-aid={`${dataAid}-schedule-type`}
          disabled={disabled}
          label="Type"
          onChange={e => onChange(TYPE_FIELD, e)}
          options={SCHEDULE_TYPES_OPTIONS}
          readOnly={readOnly}
          value={scheduleType}
        />
      </Row>
      <Row>{dateComponent}</Row>

      <Row>
        <Dropdown
          attr={`${attr}.${LOCATION_FIELD}`}
          data-aid={`${dataAid}-schedule-location`}
          disabled={disabled}
          label="Timezone"
          onChange={e => onChange(LOCATION_FIELD, e)}
          options={TIMEZONE_OPTIONS}
          readOnly={readOnly}
          searchable
          value={timeZone}
        />
      </Row>

      <MultiContentRow>
        <TimeRanges
          attr={`${attr}.${getTimeRangesField(schedule)}`}
          data-aid={`${dataAid}-schedule-timeRanges`}
          disabled={disabled}
          onAdd={onTimeRangeAdd}
          onBlur={noop}
          onChange={onTimeRangeChanged}
          onDelete={onTimeRangeDelete}
          readOnly={readOnly}
          value={timeRanges}
        />
      </MultiContentRow>
      <Row>
        <NodeSelector
          attr={`${attr}.nodeId`}
          data-aid={`${dataAid}-nextNode`}
          description="Next node in the IVR if it matches this schedule"
          disabled={disabled}
          label="Next Node"
          omittedNodeIds={omittedNodeIds}
          onChange={e => onChange(NODE_ID_FIELD, e)}
          onSelectNode={onSelectNode}
          options={options}
          readOnly={readOnly}
          searchable
          stayOnParentNodeAfterCreation
          value={nodeId}
        />
      </Row>
      <ActionRow>
        <Button data-aid={`${dataAid}-cancel`} type={ButtonTypes.TEXT} onClick={handleCancel}>
          Cancel
        </Button>

        <Button data-aid={`${dataAid}-ok`} type={Button.Types.PRIMARY} onClick={onSave}>
          Save
        </Button>
      </ActionRow>
    </>
  );

  return editMode ? renderForm() : renderShortView();
};

type ViewModeProps = {
  'data-aid'?: string;
  date: string;
  daysOfTheWeek: string[];
  disabled?: boolean;
  isOutdatedSchedule?: boolean;
  nodeId: string;
  nodeList: { label: string; value: string }[];
  onDelete?: Function;
  onSelectNode?: Function;
  setEditMode: Function;
  timeRanges: ScheduleBlock[];
  timeZone: string;
  type: ScheduleType;
};

const ViewMode: FC<ViewModeProps> = ({
  'data-aid': dataAid,
  date,
  daysOfTheWeek,
  disabled,
  isOutdatedSchedule,
  nodeId,
  nodeList,
  onDelete,
  onSelectNode,
  setEditMode,
  timeRanges,
  timeZone,
  type,
}) => {
  let ranges = '';
  if (type === ScheduleType.SCHEDULE_TYPE_WEEKDAY) {
    daysOfTheWeek = DAYS_OF_THE_WEEK.filter(day => daysOfTheWeek.includes(day));

    timeRanges = timeRanges.slice().sort((timeOne: ScheduleBlock, timeTwo: ScheduleBlock) => {
      const momentOne = moment(timeOne.start, 'hh:mm');
      const momentTwo = moment(timeTwo.start, 'hh:mm');

      return momentOne.diff(momentTwo, 'minutes');
    });

    ranges = isEmpty(timeRanges)
      ? ''
      : map(daysOfTheWeek, day => {
          const dayHours = map(timeRanges, item => {
            const start = moment(`${get(item, 'start', '')}`, 'HH:mm');
            const end = moment(`${get(item, 'end', '')}`, 'HH:mm');
            return `${day} - ${start.format(SHORT_VIEW_TIME_FORMAT)} - ${end.format(SHORT_VIEW_TIME_FORMAT)}`;
          });
          return dayHours.join(';');
        }).join(';');
  } else {
    ranges = map(timeRanges, item => {
      const start = moment(`${date} ${get(item, 'start', '')}`, STORED_DATETIME_FORMAT);
      const end = moment(`${date} ${get(item, 'end', '')}`, STORED_DATETIME_FORMAT);
      return `${start.format(SHORT_VIEW_DATE_FORMAT)} ${start.format(SHORT_VIEW_TIME_FORMAT)} - ${end.format(
        SHORT_VIEW_TIME_FORMAT
      )}`;
    }).join(';');
  }

  const context = map(ranges.split(';'), range => <div key={range}>{range}</div>);

  return (
    <ScheduleShortPresentation
      context={context}
      data-aid={`${dataAid}-extendedView-shortPresentation`}
      disabled={disabled}
      isOutdated={isOutdatedSchedule}
      onSelectNode={onSelectNode}
      node={find(nodeList, item => item.value === nodeId)}
      onDelete={onDelete}
      onSwitchToEditMode={() => setEditMode(true)}
      timeZone={timeZone}
    />
  );
};
